import { useState, useEffect, useCallback, memo } from "react";
import Navbar from "../../components/navbar";

const PRIMARY = "#326BDF";
const API_BASE_URL = "https://dpp-api.acespireconsulting.com/api";

const CATEGORY_GROUPS = {
  operatingConditions: ['operatingConditions', 'softwareInformation'],
  softwareInformation: ['operatingConditions', 'softwareInformation'],
  safetyAndRisk: ['safetyAndRisk', 'consumerInformation'],
  consumerInformation: ['safetyAndRisk', 'consumerInformation'],
  lifecycle: ['lifecycle', 'compliance'],
  compliance: ['lifecycle', 'compliance'],
};

// ── Memoized Toggle ──────────────────────────────────────────────────────────
const Toggle = memo(function Toggle({ checked, onChange, disabled }) {
  const w = 44, h = 24, circle = 18, offset = 3;
  const travel = w - circle - offset * 2;
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      style={{
        width: w, height: h, borderRadius: h,
        background: checked ? PRIMARY : "#D1D5DB",
        border: "none", cursor: disabled ? "not-allowed" : "pointer",
        padding: 0, position: "relative",
        transition: "background 0.22s cubic-bezier(.4,0,.2,1)",
        flexShrink: 0, outline: "none",
        boxShadow: checked ? `0 0 0 3px ${PRIMARY}28` : "none",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{
        position: "absolute", top: offset,
        left: checked ? offset + travel : offset,
        width: circle, height: circle, borderRadius: "50%",
        background: "#fff",
        transition: "left 0.22s cubic-bezier(.4,0,.2,1)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
      }} />
    </button>
  );
});

// ── Memoized Cell — isolates each toggle from sibling re-renders ─────────────
const AccessCell = memo(function AccessCell({ roleName, categoryKey, isChecked, isSaving, onToggle }) {
  const handleChange = useCallback(() => {
    onToggle(roleName, categoryKey, isChecked);
  }, [roleName, categoryKey, isChecked, onToggle]);

  return (
    <td style={{ padding: "16px 8px", textAlign: "center" }}>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Toggle
          checked={isChecked}
          disabled={isSaving}
          onChange={handleChange}
        />
      </div>
    </td>
  );
});

// ── Memoized Row ─────────────────────────────────────────────────────────────
const AccessRow = memo(function AccessRow({ cat, roles, accessData, saving, onToggle, accessMode, isLast }) {
  return (
    <tr
      style={{
        borderBottom: !isLast ? "1px solid #EEF2FB" : "none",
        transition: "background 0.13s",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#F5F8FF"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <td style={{
        padding: "16px 20px", fontWeight: 500,
        fontSize: 15, color: "#1F2937", whiteSpace: "nowrap",
      }}>
        {cat.display_name}
      </td>
      {roles.map(role => {
        const isChecked = accessMode === 'view'
          ? (accessData?.[role.role_name]?.[cat.category_key]?.isVisible  || false)
          : (accessData?.[role.role_name]?.[cat.category_key]?.isEditable || false);
        const isSaving = saving?.[`${role.role_name}_${cat.category_key}`] || false;
        return (
          <AccessCell
            key={role.role_name}
            roleName={role.role_name}
            categoryKey={cat.category_key}
            isChecked={isChecked}
            isSaving={isSaving}
            onToggle={onToggle}
          />
        );
      })}
    </tr>
  );
});

// ── Memoized Table ───────────────────────────────────────────────────────────
const DataTable = memo(function DataTable({ categories, roles, accessData, onToggle, saving, accessMode }) {
  if (!categories.length || !roles.length) {
    return (
      <div style={{
        background: "#fff", borderRadius: 10, padding: 40,
        textAlign: "center", color: "#9CA3AF", fontSize: 15,
        border: "1px solid #E2EAF8"
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{
      background: "#fff", borderRadius: 10,
      boxShadow: "0 1px 6px rgba(50,107,223,0.08)",
      overflow: "hidden", border: "1px solid #E2EAF8", width: "100%",
    }}>
      <div style={{ overflowX: "auto", width: "100%" }}>
        <table style={{
          width: "100%", borderCollapse: "collapse",
          minWidth: 600, tableLayout: "fixed"
        }}>
          <thead>
            <tr style={{ background: "#D6E1F9" }}>
              <th style={{
                padding: "14px 20px", textAlign: "left",
                fontWeight: 700, fontSize: 15, color: "#1F2937",
                whiteSpace: "nowrap", width: "22%", borderBottom: "none",
              }}>
                Features
              </th>
              {roles.map(role => (
                <th key={role.role_name} style={{
                  padding: "14px 8px", textAlign: "center",
                  fontWeight: 700, fontSize: 15, color: "#1F2937",
                  whiteSpace: "nowrap", borderBottom: "none",
                }}>
                  {role.role_name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat, i) => (
              <AccessRow
                key={cat.category_key}
                cat={cat}
                roles={roles}
                accessData={accessData}
                saving={saving}
                onToggle={onToggle}
                accessMode={accessMode}
                isLast={i === categories.length - 1}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// ── Main Page ────────────────────────────────────────────────────────────────
export default function RoleBasedAccess() {
  const [activeMainTab, setActiveMainTab]     = useState("dppData");
  const [activeAccessTab, setActiveAccessTab] = useState("view");

  const [roles, setRoles]                                     = useState([]);
  const [dppCategories, setDppCategories]                     = useState([]);
  const [transactionalCategories, setTransactionalCategories] = useState([]);
  const [accessData, setAccessData]                           = useState({});
  const [saving, setSaving]                                   = useState({});
  const [loading, setLoading]                                 = useState(true);
  const [error, setError]                                     = useState(null);

  // ── Fetch roles + access data once on mount ──
  useEffect(() => {
    const fetchBase = async () => {
      try {
        setLoading(true);
        setError(null);

        const [rolesRes, accessRes] = await Promise.all([
          fetch(`${API_BASE_URL}/roles`),
          fetch(`${API_BASE_URL}/role-access`)
        ]);

        const [rolesData, accessJson] = await Promise.all([
          rolesRes.json(),
          accessRes.json()
        ]);

        if (rolesData.success) {
          setRoles(rolesData.data.filter(r => r.role_name !== 'Manufacturer'));
        }
        if (accessJson.success) setAccessData(accessJson.data);

      } catch (err) {
        console.error('Error loading base data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchBase();
  }, []);

  // ── Fetch categories when main tab changes ──
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const type = activeMainTab === 'dppData' ? 'dpp' : 'transactional';
        if (type === 'dpp'           && dppCategories.length > 0)           return;
        if (type === 'transactional' && transactionalCategories.length > 0) return;

        const res  = await fetch(`${API_BASE_URL}/dpp-categories?type=${type}`);
        const data = await res.json();

        if (data.success) {
          if (type === 'dpp') setDppCategories(data.data);
          else setTransactionalCategories(data.data);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, [activeMainTab]);

  // ── Reset to "view" tab when switching to Transactional Data ──
  useEffect(() => {
    if (activeMainTab === "transactionalData") {
      setActiveAccessTab("view");
    }
  }, [activeMainTab]);

  const currentCategories = activeMainTab === 'dppData' ? dppCategories : transactionalCategories;

  // ── Handle View Access toggle (is_visible) ──
  const handleViewToggle = useCallback(async (roleName, categoryKey, currentValue) => {
    const newValue     = !currentValue;
    const keysToUpdate = CATEGORY_GROUPS[categoryKey] || [categoryKey];

    // Optimistically update ALL grouped keys
    setAccessData(prev => {
      const updated = { ...prev, [roleName]: { ...prev[roleName] } };
      keysToUpdate.forEach(key => {
        updated[roleName][key] = { ...updated[roleName][key], isVisible: newValue };
      });
      return updated;
    });

    // Mark all grouped keys as saving
    setSaving(prev => {
      const s = { ...prev };
      keysToUpdate.forEach(key => { s[`${roleName}_${key}`] = true; });
      return s;
    });

    try {
      const response = await fetch(`${API_BASE_URL}/role-access`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role_name: roleName, category_key: categoryKey, is_visible: newValue })
      });
      const result = await response.json();

      if (!result.success) {
        // Rollback ALL grouped keys
        setAccessData(prev => {
          const updated = { ...prev, [roleName]: { ...prev[roleName] } };
          keysToUpdate.forEach(key => {
            updated[roleName][key] = { ...updated[roleName][key], isVisible: currentValue };
          });
          return updated;
        });
      }
    } catch (err) {
      // Rollback on network error
      setAccessData(prev => {
        const updated = { ...prev, [roleName]: { ...prev[roleName] } };
        keysToUpdate.forEach(key => {
          updated[roleName][key] = { ...updated[roleName][key], isVisible: currentValue };
        });
        return updated;
      });
      console.error('Error updating view toggle:', err);
    } finally {
      setSaving(prev => {
        const s = { ...prev };
        keysToUpdate.forEach(key => { s[`${roleName}_${key}`] = false; });
        return s;
      });
    }
  }, []);

  // ── Handle Edit Access toggle (is_editable) ──
  const handleEditToggle = useCallback(async (roleName, categoryKey, currentValue) => {
    const newValue     = !currentValue;
    const keysToUpdate = CATEGORY_GROUPS[categoryKey] || [categoryKey];

    // Optimistically update ALL grouped keys
    setAccessData(prev => {
      const updated = { ...prev, [roleName]: { ...prev[roleName] } };
      keysToUpdate.forEach(key => {
        updated[roleName][key] = { ...updated[roleName][key], isEditable: newValue };
      });
      return updated;
    });

    // Mark all grouped keys as saving
    setSaving(prev => {
      const s = { ...prev };
      keysToUpdate.forEach(key => { s[`${roleName}_${key}`] = true; });
      return s;
    });

    try {
      const response = await fetch(`${API_BASE_URL}/role-access`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role_name: roleName, category_key: categoryKey, is_editable: newValue })
      });
      const result = await response.json();

      if (!result.success) {
        // Rollback ALL grouped keys
        setAccessData(prev => {
          const updated = { ...prev, [roleName]: { ...prev[roleName] } };
          keysToUpdate.forEach(key => {
            updated[roleName][key] = { ...updated[roleName][key], isEditable: currentValue };
          });
          return updated;
        });
      }
    } catch (err) {
      // Rollback on network error
      setAccessData(prev => {
        const updated = { ...prev, [roleName]: { ...prev[roleName] } };
        keysToUpdate.forEach(key => {
          updated[roleName][key] = { ...updated[roleName][key], isEditable: currentValue };
        });
        return updated;
      });
      console.error('Error updating edit toggle:', err);
    } finally {
      setSaving(prev => {
        const s = { ...prev };
        keysToUpdate.forEach(key => { s[`${roleName}_${key}`] = false; });
        return s;
      });
    }
  }, []);

  // ── Tabs: hide "Edit Access" for Transactional Data ──
  const accessTabs = activeMainTab === "transactionalData"
    ? [{ key: "view", label: "View Access" }]
    : [{ key: "view", label: "View Access" }, { key: "edit", label: "Edit Access" }];

  return (
    <div style={{
      minHeight: "100vh", background: "#F3F5FA",
      fontFamily: "'Segoe UI', sans-serif", padding: 0, margin: 0,
    }}>
      <Navbar />

      <div style={{
        padding: "20px clamp(16px, 4vw, 40px) 32px",
        boxSizing: "border-box", display: "flex",
        flexDirection: "column", gap: 16,
      }}>

        {/* Section 1 — Header */}
        <div style={{
          background: "#fff", border: "1.5px solid #E2EAF8",
          borderRadius: 10, padding: "0 16px",
          display: "flex", alignItems: "center",
          justifyContent: "space-between", height: 52,
          boxSizing: "border-box",
          boxShadow: "0 1px 4px rgba(50,107,223,0.05)",
        }}>
          <button style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "none", border: "none", color: PRIMARY,
            fontWeight: 600, fontSize: 15, cursor: "pointer",
            padding: "6px 0", flexShrink: 0,
          }}>
            <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
              <path d="M12 5l-5 5 5 5" stroke={PRIMARY} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>

          <div style={{ display: "flex", alignItems: "flex-end", height: "100%" }}>
            {[{ key: "dppData", label: "Dpp Data" }, { key: "transactionalData", label: "Transactional Data" }].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveMainTab(tab.key)}
                style={{
                  padding: "0 18px", height: "100%",
                  background: "none", border: "none",
                  borderBottom: activeMainTab === tab.key ? `2.5px solid ${PRIMARY}` : "2.5px solid transparent",
                  color: activeMainTab === tab.key ? PRIMARY : "#6B7280",
                  fontWeight: activeMainTab === tab.key ? 700 : 500,
                  fontSize: 15, cursor: "pointer",
                  transition: "all 0.18s", whiteSpace: "nowrap",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <span style={{ color: PRIMARY, fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
            Role based info
          </span>
        </div>

        {/* Section 2 — View / Edit tabs */}
        <div style={{ display: "flex", borderBottom: "1.5px solid #E2EAF8", padding: "0 4px" }}>
          {accessTabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveAccessTab(t.key)}
              style={{
                padding: "10px 18px", background: "none", border: "none",
                borderBottom: activeAccessTab === t.key ? `2.5px solid ${PRIMARY}` : "2.5px solid transparent",
                color: activeAccessTab === t.key ? PRIMARY : "#6B7280",
                fontWeight: activeAccessTab === t.key ? 700 : 500,
                fontSize: 15, cursor: "pointer",
                transition: "all 0.18s", marginBottom: -1.5, whiteSpace: "nowrap",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Section 3 — Table */}
        {loading ? (
          <div style={{
            background: "#fff", borderRadius: 10, padding: 60,
            textAlign: "center", color: "#6B7280", fontSize: 15,
            border: "1px solid #E2EAF8"
          }}>
            Loading role access data...
          </div>
        ) : error ? (
          <div style={{
            background: "#FEF2F2", borderRadius: 10, padding: 40,
            textAlign: "center", color: "#991B1B", fontSize: 15,
            border: "1px solid #FECACA"
          }}>
            {error}
          </div>
        ) : (
          <DataTable
            categories={currentCategories}
            roles={roles}
            accessData={accessData}
            onToggle={activeAccessTab === 'view' ? handleViewToggle : handleEditToggle}
            saving={saving}
            accessMode={activeAccessTab}
          />
        )}

        {/* Footer */}
        <p style={{ margin: 0, fontSize: 12, color: "#9CA3AF", textAlign: "right" }}>
          Changes are saved automatically · {activeAccessTab === "view" ? "Viewing" : "Editing"} {activeMainTab === "dppData" ? "DPP Data" : "Transactional Data"} access
        </p>

      </div>
    </div>
  );
}